/**
 * Cookie utility functions for token management
 */

import { getEnvVar } from '../api/ApiUrls';

/**
 * Extracts the parent domain from the current URL to share cookies across subdomains
 * @returns Parent domain with leading dot (e.g., '.ideoz.ai') or empty string for localhost
 */
const getParentDomain = (): string => {
    if (typeof window === 'undefined') return '';

    try {
        const hostname = window.location.hostname;

        // For localhost or IP addresses, don't set domain
        if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
            return '';
        }

        // Split hostname by dots
        const parts = hostname.split('.');

        // If it's already a top-level domain (e.g., ideoz.ai), use it with leading dot
        if (parts.length <= 2) {
            return `.${hostname}`;
        }

        // For subdomains (e.g., admin.ideoz.ai), extract parent domain
        const parentDomain = parts.slice(-2).join('.');
        return `.${parentDomain}`;
    } catch (e) {
        // Silently fail - window.location not available in plugin environment
        return '';
    }
};

/**
 * Sets a cookie with the specified name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30)
 */
export const setCookie = (name: string, value: string, days: number = 30): void => {
    if (typeof window === 'undefined') return;

    try {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        const isSecure = window.location.protocol === 'https:';
        const domain = getParentDomain();
        const domainAttribute = domain ? `;domain=${domain}` : '';
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure ? ';Secure' : ''}${domainAttribute}`;
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
};

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
    if (typeof window === 'undefined') return null;

    try {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
    return null;
};

/**
 * Removes a cookie by name
 * @param name - Cookie name
 */
export const removeCookie = (name: string): void => {
    if (typeof window === 'undefined') return;

    try {
        const domain = getParentDomain();
        const domainAttribute = domain ? `;domain=${domain}` : '';
        // Remove with current domain attribute
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttribute};`;
        // Also try removing without domain to handle cross-subdomain cookie mismatch
        if (domainAttribute) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    } catch (e) {
        // Silently fail - cookies not available in plugin environment
    }
};

/**
 * Token-specific cookie utilities
 * Cookie prefix is configurable via VITE_COOKIE_PREFIX env var (defaults to 'i')
 */
const COOKIE_PREFIX = getEnvVar('VITE_COOKIE_PREFIX') || 'i';
const TOKEN_COOKIE_KEY = `${COOKIE_PREFIX || 'i'}_a_t`;
const REFRESH_TOKEN_COOKIE_KEY = `${COOKIE_PREFIX || 'i'}_r_t`;

/**
 * Saves the access token to memory (window) + a secure, domain-shared cookie.
 */
export const saveAccessToken = (token: string): void => {
    if (typeof window !== 'undefined') {
        (window as any).appAccessToken = token;
    }

    try {
        setCookie(TOKEN_COOKIE_KEY, token, 30);
    } catch (e) {
        console.debug('Cookie storage not available:', e);
    }
    // Verify cookie was actually written (detect silent write failures)
    try {
        const verified = getCookie(TOKEN_COOKIE_KEY);
        if (!verified) {
            console.warn('⚠️ Access token cookie write failed (possible quota/3rd-party blocking). Token only in memory.');
        }
    } catch (e) {
        // Verification failed, token is still in window.appAccessToken
    }
};

/**
 * Saves the refresh token to memory (window) + a secure, domain-shared cookie.
 * 365 days to match the server-side refresh token lifetime (1 year).
 */
export const saveRefreshToken = (token: string): void => {
    if (typeof window !== 'undefined') {
        (window as any).appRefreshToken = token;
    }

    try {
        setCookie(REFRESH_TOKEN_COOKIE_KEY, token, 365);
    } catch (e) {
        console.debug('Cookie storage not available:', e);
    }
};

/**
 * Gets access token from cookie (web) or localStorage (plugin)
 * For web: Reads from secure, domain-shared cookies
 * For plugin: Reads from localStorage
 * @returns Access token or null if not found
 */
export const getAccessToken = (): string | null => {
    try {
        const cookieToken = getCookie(TOKEN_COOKIE_KEY);
        if (cookieToken) {
            return cookieToken;
        }
    } catch (e) {
        console.debug('Cookie read not available:', e);
    }
    return null;
};

/**
 * Gets refresh token from cookie (web) or window/localStorage (plugin)
 * For web: Reads from secure, domain-shared cookies
 * For plugin: Reads from window or localStorage
 * @returns Refresh token or null if not found
 */
export const getRefreshToken = (): string | null => {
    try {
        const cookieToken = getCookie(REFRESH_TOKEN_COOKIE_KEY);
        if (cookieToken) {
            return cookieToken;
        }
    } catch (e) {
        console.debug('Cookie read not available:', e);
    }
    return null;
};

/**
 * Removes access token and refresh token from all storage (full auth clear)
 * For web: Removes from cookies
 * For plugin: Removes from window and localStorage
 */
export const removeAccessToken = (): void => {
    try {
        removeCookie(TOKEN_COOKIE_KEY);
        removeCookie(REFRESH_TOKEN_COOKIE_KEY);
    } catch (e) {
        console.debug('Cookie removal not available:', e);
    }

    if (typeof window !== 'undefined') {
        delete (window as any).appAccessToken;
        delete (window as any).appRefreshToken;
    }
};

/**
 * Removes only the refresh token from cookies/window/localStorage (access token unchanged)
 * Gracefully handles errors in restricted environments
 */
export const removeRefreshToken = (): void => {
    try {
        removeCookie(REFRESH_TOKEN_COOKIE_KEY);
    } catch (e) {
        console.debug('Cookie removal not available:', e);
    }

    if (typeof window !== 'undefined') {
        delete (window as any).appRefreshToken;
    }
};

